import {
  DEFAULT_API_PAYMENT_METHOD_CODE,
  isPaymentMethodCode,
  type PaymentMethodCode,
} from '@/constants/deposit';
import { type CashierMethodTile, type RailInputField } from '@/schemas/methods.schema';

export interface KnownApiRail {
  code: PaymentMethodCode;
  name: string;
  inputFields?: RailInputField[] | undefined;
}

function isApiTile(tile: CashierMethodTile): tile is Extract<CashierMethodTile, { kind: 'api' }> {
  return tile.kind === 'api';
}

/**
 * Known wallet rails on an API tile, in catalogue order. Unknown codes are dropped so a future
 * rail does not fail the methods parse; an empty result means "use the platform default".
 */
export function railsOnApiTile(tile: CashierMethodTile | null | undefined): KnownApiRail[] {
  if (tile === null || tile === undefined || !isApiTile(tile) || tile.rails === undefined) {
    return [];
  }
  const known: KnownApiRail[] = [];
  for (const rail of tile.rails) {
    if (!isPaymentMethodCode(rail.code)) continue;
    if (known.some((entry) => entry.code === rail.code)) continue;
    known.push({
      code: rail.code,
      name: rail.name,
      ...(rail.inputFields !== undefined ? { inputFields: rail.inputFields } : {}),
    });
  }
  return known;
}

export function needsRailChoice(tile: CashierMethodTile | null | undefined): boolean {
  return railsOnApiTile(tile).length > 1;
}

/**
 * `paymentMethodCode` to send on choose. `null` means the player still has to pick — Continue
 * must stay disabled. A single known rail is used without a tap; missing rails fall back to
 * `DEFAULT_API_PAYMENT_METHOD_CODE`.
 */
export function resolvedPaymentMethodCode(
  tile: CashierMethodTile | null | undefined,
  chosen: PaymentMethodCode | null,
): PaymentMethodCode | null {
  if (tile === null || tile === undefined || !isApiTile(tile)) return null;
  const rails = railsOnApiTile(tile);
  if (rails.length > 1) {
    if (chosen === null) return null;
    return rails.some((rail) => rail.code === chosen) ? chosen : null;
  }
  const only = rails[0];
  return only === undefined ? DEFAULT_API_PAYMENT_METHOD_CODE : only.code;
}

export function railNameOnApiTile(
  tile: CashierMethodTile | null | undefined,
  code: PaymentMethodCode,
): string | null {
  const rail = railsOnApiTile(tile).find((entry) => entry.code === code);
  if (rail === undefined) return null;
  return rail.name.trim() === '' ? null : rail.name;
}
